import { getSupabase } from "../auth/supabaseClient";

const REFERRAL_STORAGE_KEY = "bb_referral";
const REFERRAL_GEMS = 25;
const MAX_REFERRALS = 10;
const REFERRAL_LEVEL_THRESHOLD = 5;

class ReferralService {
  /** Capture ?ref=username from URL and store in localStorage. */
  captureReferral(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref && ref.trim().length > 0) {
        localStorage.setItem(REFERRAL_STORAGE_KEY, ref.trim().toLowerCase());
        // Clean the URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete("ref");
        window.history.replaceState({}, "", url.toString());
      }
    } catch {
      // no-op
    }
  }

  /** Get stored referral username (set during signup flow). */
  getPendingReferral(): string | null {
    try {
      return localStorage.getItem(REFERRAL_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** Consume the pending referral and write to profile. Called on account creation. */
  async applyReferral(userId: string): Promise<void> {
    const referrer = this.getPendingReferral();
    if (!referrer) return;

    try {
      const supabase = getSupabase();
      // Verify referrer exists and isn't self-referral
      const { data: referrerProfile } = await supabase
        .from("profiles")
        .select("id, username")
        .ilike("username", referrer)
        .single();

      if (!referrerProfile || referrerProfile.id === userId) {
        this.clearPendingReferral();
        return;
      }

      await supabase
        .from("profiles")
        .update({ referred_by: referrer })
        .eq("id", userId);

      this.clearPendingReferral();
    } catch {
      // Silent fail — referral is best-effort
    }
  }

  /**
   * Check if the referred player has reached the threshold level
   * and grant rewards to both players. Called after XP gain.
   */
  async checkAndRewardReferral(userId: string, currentLevel: number): Promise<boolean> {
    if (currentLevel < REFERRAL_LEVEL_THRESHOLD) return false;

    try {
      const supabase = getSupabase();

      // Check if this user was referred and hasn't been rewarded yet
      const { data: profile } = await supabase
        .from("profiles")
        .select("referred_by, referral_rewarded")
        .eq("id", userId)
        .single();

      if (!profile?.referred_by || profile.referral_rewarded) return false;

      // Find referrer
      const { data: referrer } = await supabase
        .from("profiles")
        .select("id, referral_count")
        .ilike("username", profile.referred_by)
        .single();

      if (!referrer || referrer.referral_count >= MAX_REFERRALS) {
        // Referrer at cap — still reward the referred player
        await supabase.rpc("credit_gems", { p_user_id: userId, p_amount: REFERRAL_GEMS });
        await supabase
          .from("profiles")
          .update({ referral_rewarded: true })
          .eq("id", userId);
        return true;
      }

      // Credit both players
      await supabase.rpc("credit_gems", { p_user_id: userId, p_amount: REFERRAL_GEMS });
      await supabase.rpc("credit_gems", { p_user_id: referrer.id, p_amount: REFERRAL_GEMS });

      // Mark rewarded + increment referrer count
      await supabase
        .from("profiles")
        .update({ referral_rewarded: true })
        .eq("id", userId);
      await supabase
        .from("profiles")
        .update({ referral_count: referrer.referral_count + 1 })
        .eq("id", referrer.id);

      return true;
    } catch {
      return false;
    }
  }

  /** Get referral stats for display. */
  async getReferralStats(userId: string): Promise<{ count: number; max: number }> {
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("profiles")
        .select("referral_count")
        .eq("id", userId)
        .single();
      return { count: data?.referral_count ?? 0, max: MAX_REFERRALS };
    } catch {
      return { count: 0, max: MAX_REFERRALS };
    }
  }

  /** Build the referral link for sharing. */
  buildReferralLink(username: string): string {
    const base = window.location.origin;
    return `${base}/?ref=${encodeURIComponent(username)}`;
  }

  /** Share via Web Share API, fallback to clipboard. Returns true if shared/copied. */
  async shareReferralLink(username: string): Promise<boolean> {
    const link = this.buildReferralLink(username);
    const shareData = {
      title: "BitBeast BlockBreaker",
      text: "Play BitBeast BlockBreaker with me! We both get 25 gems!",
      url: link,
    };

    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return true;
      }
    } catch {
      // User cancelled or share failed — fall through to clipboard
    }

    // Clipboard fallback (works on HTTP too)
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      return true;
    } catch {
      return false;
    }
  }

  private clearPendingReferral(): void {
    try {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
    } catch {
      // no-op
    }
  }
}

export const referralService = new ReferralService();
